import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { ArrowLeft } from 'lucide-react'

import Alert from '@/components/ui/Alert'
import Button from '@/components/ui/Button'
import Input from '@/components/ui/Input'
import Label from '@/components/ui/Label'
import InvitationModal from '@/features/admin/invitation-modal'
import { useCreateUser } from '@/features/admin/use-admin-users'
import { useJobPositions } from '@/features/admin/use-job-positions'
import { apiErrorMessage } from '@/lib/api'
import { useCanManageAdmins } from '@/lib/acl'

const schema = z.object({
  email: z
    .string()
    .min(1, 'Wpisz email')
    .email('Nieprawidłowy format email'),
  name: z
    .string()
    .min(1, 'Wpisz imię i nazwisko')
    .max(120, 'Maksymalnie 120 znaków'),
  role: z.enum(['EMPLOYEE', 'ADMIN']),
  positionId: z.string(),
  hasChildrenUnder14: z.boolean(),
  hourlyRate: z.number().min(0).max(999999.99).nullable(),
})

type FormValues = z.infer<typeof schema>

export default function UserCreate() {
  const navigate = useNavigate()
  const [shownInvitation, setShownInvitation] = useState<{
    name: string
    email: string
    invitationLink: string
    expiresAt: string
  } | null>(null)

  const canManageAdmins = useCanManageAdmins()
  const create = useCreateUser()
  const positionsQuery = useJobPositions()
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      email: '',
      name: '',
      role: 'EMPLOYEE',
      positionId: '',
      hasChildrenUnder14: false,
      hourlyRate: null,
    },
  })

  async function onSubmit(values: FormValues) {
    try {
      const result = await create.mutateAsync({
        email: values.email,
        name: values.name,
        role: values.role,
        positionId: values.positionId || null,
        hasChildrenUnder14: values.hasChildrenUnder14,
        hourlyRate: values.hourlyRate,
      })
      setShownInvitation({
        name: result.user.name,
        email: result.user.email,
        invitationLink: result.invitationLink,
        expiresAt: result.expiresAt,
      })
    } catch {
      // error renderowany niżej z create.error
    }
  }

  function handleClose() {
    setShownInvitation(null)
    navigate('/admin/users', { replace: true })
  }

  return (
    <>
      <div className="max-w-md">
        <Link
          to="/admin/users"
          className="text-sm text-text-muted hover:text-text-primary mb-4 inline-flex items-center gap-1"
        >
          <ArrowLeft className="h-3.5 w-3.5" aria-hidden />
          Wróć do listy
        </Link>
        <h1 className="text-2xl font-semibold mb-6">Dodaj pracownika</h1>

        <form
          onSubmit={form.handleSubmit(onSubmit)}
          noValidate
          className="space-y-4"
        >
          <div>
            <Label htmlFor="name">Imię i nazwisko</Label>
            <Input
              id="name"
              autoFocus
              autoComplete="off"
              invalid={!!form.formState.errors.name}
              {...form.register('name')}
            />
            {form.formState.errors.name && (
              <p className="mt-1 text-xs text-danger">
                {form.formState.errors.name.message}
              </p>
            )}
          </div>

          <div>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              autoComplete="off"
              invalid={!!form.formState.errors.email}
              {...form.register('email')}
            />
            {form.formState.errors.email && (
              <p className="mt-1 text-xs text-danger">
                {form.formState.errors.email.message}
              </p>
            )}
          </div>

          <div>
            <Label>Rola</Label>
            {canManageAdmins ? (
              <div className="flex gap-4 mt-1">
                <label className="flex items-center gap-2 cursor-pointer text-sm">
                  <input
                    type="radio"
                    value="EMPLOYEE"
                    {...form.register('role')}
                  />
                  Pracownik
                </label>
                <label className="flex items-center gap-2 cursor-pointer text-sm">
                  <input
                    type="radio"
                    value="ADMIN"
                    {...form.register('role')}
                  />
                  Admin
                </label>
              </div>
            ) : (
              <p className="mt-1 text-sm text-text-muted">
                Pracownik{' '}
                <span className="text-text-faint text-xs">
                  (tylko admin może tworzyć konta admina)
                </span>
              </p>
            )}
          </div>

          <div>
            <Label htmlFor="positionId">Stanowisko (opcjonalne)</Label>
            <select
              id="positionId"
              disabled={positionsQuery.isLoading}
              className="mt-1 w-full rounded-card border border-border-subtle bg-bg-elevated px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-accent disabled:opacity-50"
              {...form.register('positionId')}
            >
              <option value="">— Brak —</option>
              {(positionsQuery.data ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <Label htmlFor="hourlyRate">
              Stawka godzinowa (PLN/h netto, opcjonalnie)
            </Label>
            <div className="mt-1 relative">
              <Input
                id="hourlyRate"
                type="number"
                step="0.01"
                min="0"
                max="999999.99"
                placeholder="np. 75.00"
                className="pr-12"
                {...form.register('hourlyRate', {
                  setValueAs: (v) =>
                    v === '' || v === null || v === undefined
                      ? null
                      : Number(v),
                })}
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-text-faint pointer-events-none">
                zł/h
              </span>
            </div>
            <p className="mt-1 text-xs text-text-muted">
              Używana do szacunku miesięcznej pensji w raporcie.
            </p>
          </div>

          <div>
            <Label>Uprawnienia urlopowe</Label>
            <label className="mt-1 flex items-start gap-3 rounded-card border border-border-subtle bg-bg-surface p-3 cursor-pointer">
              <input
                type="checkbox"
                {...form.register('hasChildrenUnder14')}
                className="mt-0.5 h-4 w-4 rounded border-border-strong text-brand-accent focus:ring-brand-accent"
              />
              <span className="flex-1">
                <span className="text-sm font-medium text-text-primary">
                  Pracownik ma dziecko do 14 r.ż.
                </span>
                <span className="mt-0.5 block text-xs text-text-muted">
                  Uprawnienie do urlopu „opieka nad dzieckiem" (art. 188 KP —
                  2 dni/rok, 100% płatne). Pracownik bez tej flagi nie widzi
                  tej opcji w aplikacji.
                </span>
              </span>
            </label>
          </div>

          {create.error && (
            <Alert variant="error">{apiErrorMessage(create.error)}</Alert>
          )}

          <Button
            type="submit"
            loading={create.isPending}
            className="w-full"
          >
            Utwórz konto
          </Button>
        </form>
      </div>

      {shownInvitation && (
        <InvitationModal
          name={shownInvitation.name}
          email={shownInvitation.email}
          invitationLink={shownInvitation.invitationLink}
          expiresAt={shownInvitation.expiresAt}
          onClose={handleClose}
        />
      )}
    </>
  )
}
